import { RecordValue } from "libs/schema";
import { toast } from "~/environment/toast-service";
import { ClientEnvironment } from "~/environment/ClientEnvironment";
import { withActionContext, write } from "./write";
import * as actions from "libs/actions";

export type UpdateUserSettingsProps = {
  settings: RecordValue<"user_settings">["settings"];
  /**
   * Sometimes we update settings without the user initiating the action, in that case we want to disable undo since
   * the user has no idea this action is happening. A current example of this is the KBarDialog
   */
  disableUndo?: true;
};

export const updateUserSettings = withActionContext(async (context, environment, props: UpdateUserSettingsProps) => {
  const { settings, disableUndo } = props;
  const [originalSettings] = await context.environment.loader.getRecord("user_settings", context.currentUserId);

  return {
    forward: async () => {
      const action = await actions.updateUserSettings.updateUserSettings.action(
        {
          settings,
        },
        context,
      );

      await write(environment, { action });
    },
    backward:
      disableUndo ? undefined : (
        async () => {
          if (!originalSettings) {
            throw new actions.InvalidActionStateError(`[updateUserSettings] No original settings found.`, {
              errorData: { userId: context.currentUserId },
            });
          }

          const action = await actions.updateUserSettings.updateUserSettings.action(
            {
              settings: originalSettings.settings,
            },
            context,
          );

          await write(environment, {
            action,
            onOptimisticWrite: () => {
              toast("vanilla", {
                subject: "Undoing user settings changes.",
              });
            },
          });
        }
      ),
  };
});

export async function updateScheduledDelivery(environment: ClientEnvironment, enableScheduledDelivery: boolean) {
  const currentUserId = environment.auth.getAndAssertCurrentUserId();
  const [settingsRecord] = await environment.recordLoader.getRecord("user_settings", currentUserId);

  const settings = settingsRecord?.settings;

  const hasTheUserEverEnabledScheduledDeliveryBefore =
    !!settings?.scheduled_days &&
    !!settings.scheduled_times &&
    settings.scheduled_days.length > 0 &&
    settings.scheduled_times.length > 0;

  if (hasTheUserEverEnabledScheduledDeliveryBefore) {
    updateUserSettings(environment, {
      settings: { enable_scheduled_delivery: enableScheduledDelivery },
    });

    toast("vanilla", {
      subject: `Scheduled delivery ${enableScheduledDelivery ? "enabled" : "disabled"}`,
    });
  } else {
    updateUserSettings(environment, {
      settings: {
        enable_scheduled_delivery: true,
      },
    });
  }
}
